import { SearchInputTypes } from '@genial/components';
import { useBreakpointValue } from '@genial/utils';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../../hooks/useRecoilQuery';
import { customersQuery } from '../../../../../store/batchTicket/selectors';
import { customerConsortiumSectionStatusState } from '../../../../../store/consortium/atom';
import { masterStateSelectedAdvisorSelector } from '../../../../../store/master/selector';
import { CustomerItemDataProps, orderByProps } from '../../../utils/types';

export const useCustomer = () => {
  const customerSectionStatus = useRecoilValue(customerConsortiumSectionStatusState);
  const selectedAdvisor = useRecoilValue(masterStateSelectedAdvisorSelector);
  const isMobile = useBreakpointValue({ base: true, sm: false });

  const [allCustomers, setAllCustomers] = useState<CustomerItemDataProps[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState<orderByProps>({
    value: 'name',
    direction: 'desc',
  });

  const {
    data: response,
    error,
    loading: isLoading,
    refresher,
  } = useRecoilQuery(customersQuery({ page, searchTerm }));

  const isFirstPage = page === 1;

  const handleEndReached = () => {
    if (!response?.meta?.pagination.hasNext || isLoading) {
      return;
    }
    setPage((prev) => prev + 1);
  };

  const handleClearSearchTerm = () => {
    setSearchTerm('');
    setPage(1);
  };

  const handleSetSearchTerm = (term: string) => {
    setSearchTerm(term);
    setAllCustomers([]);
    setPage(1);
  };

  const handleOrderBy = (value?: string) => {
    if (!value) {
      return;
    }

    setOrderBy((currentData) => {
      if (currentData.value !== value) {
        return { value, direction: 'desc' };
      }

      const direction = currentData.direction === 'asc' ? 'desc' : 'asc';

      return { value, direction };
    });
  };

  const searchInputProps: SearchInputTypes.Props = {
    hStackProps: { mb: 0 },
    onClear: handleClearSearchTerm,
    onSearch: handleSetSearchTerm,
    isLoading,
    placeholder: 'Pesquise por nome, conta ou CPF',
    isMobile,
  };

  const optionsTitle = {
    opened: 'Selecione o cliente',
    closed: 'Cliente selecionado',
    edit: 'Selecione o cliente',
  };

  const title = optionsTitle[customerSectionStatus];

  useEffect(() => {
    if (!response?.data) {
      return;
    }

    if (page === 1) {
      setAllCustomers(response.data);
    } else {
      setAllCustomers((prev) => [...prev, ...response.data]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response?.data]);

  useEffect(() => {
    setAllCustomers([]);
    setPage(1);
  }, [selectedAdvisor]);

  return {
    searchInputProps,
    orderBy,
    refresher,
    error,
    title,
    isLoading: isLoading && isFirstPage,
    isLoadingMore: isLoading && !isFirstPage,
    onEndReached: handleEndReached,
    skeletonItemCount: 6,
    onOrderBy: handleOrderBy,
    response: { meta: response?.meta, data: allCustomers },
  };
};
